/**
 * FAQ Page Component
 */

import { memo } from 'react';

const faqs = [
  {
    category: 'General',
    questions: [
      {
        question: 'What is ImageLab?',
        answer: 'ImageLab is a free online toolkit for working with images and SVG files. It includes tools for converting SVG to code, PNG to SVG conversion, favicon generation, image compression, format conversion, and SVG editing.',
      },
      {
        question: 'Is ImageLab free to use?',
        answer: 'Yes! ImageLab is completely free to use for both personal and commercial projects. There are no usage limits, no sign-up required, and no watermarks on your output.',
      },
      {
        question: 'Is my data secure?',
        answer: 'Absolutely! All file processing happens locally in your browser. Your files are never uploaded to any server, ensuring complete privacy and security for your designs.',
      },
    ],
  },
  {
    category: 'SVG to Code',
    questions: [
      {
        question: 'What is an SVG file?',
        answer: 'SVG (Scalable Vector Graphics) is an XML-based image format for two-dimensional graphics. Unlike raster images (PNG, JPG), SVGs can scale to any size without losing quality, making them perfect for logos, icons, and illustrations on the web.',
      },
      {
        question: 'Why should I convert SVG to code?',
        answer: 'Embedding SVG code directly in your HTML allows for better performance (no additional HTTP requests), easier styling with CSS, JavaScript manipulation, and smaller file sizes. It also enables features like animations and interactivity.',
      },
      {
        question: 'How do I use the converted SVG code?',
        answer: 'Simply copy the generated code and paste it directly into your HTML file where you want the SVG to appear. You can also save it as a separate file and reference it using an <img> tag or CSS background.',
      },
    ],
  },
  {
    category: 'PNG to SVG',
    questions: [
      {
        question: 'How does PNG to SVG conversion work?',
        answer: 'We use advanced image tracing algorithms to convert raster images (pixels) into vector graphics (paths). The tool analyzes your image and creates scalable vector shapes that closely match the original.',
      },
      {
        question: 'What types of images work best for PNG to SVG conversion?',
        answer: 'Images with clear edges, solid colors, and simple shapes work best. Logos, icons, illustrations, and line art typically produce excellent results. Complex photographs may not convert as cleanly.',
      },
      {
        question: 'Can I convert JPG or other formats to SVG?',
        answer: 'Yes! The PNG to SVG converter supports various raster formats including PNG, JPG, GIF, WebP, and BMP.',
      },
    ],
  },
  {
    category: 'Favicon Generator',
    questions: [
      {
        question: 'What sizes does the favicon generator create?',
        answer: 'The generator creates all standard favicon sizes: 16x16, 32x32, 48x48, 64x64, 128x128, 192x192, and 512x512 pixels. It also creates an ICO file containing multiple sizes.',
      },
      {
        question: 'What is included in the favicon package?',
        answer: 'The package includes PNG files in all sizes, a multi-resolution ICO file, apple-touch-icon, Android Chrome icons, and ready-to-use manifest files (site.webmanifest and browserconfig.xml).',
      },
      {
        question: 'What image format should I use for favicon generation?',
        answer: 'We recommend using a square PNG or SVG image with a minimum size of 512x512 pixels for best results. The tool will automatically resize your image to all required sizes.',
      },
    ],
  },
  {
    category: 'Image Compression',
    questions: [
      {
        question: 'How much can I reduce my image file size?',
        answer: 'Depending on the original image and quality settings, you can typically reduce file sizes by 40-80% while maintaining good visual quality.',
      },
      {
        question: 'Does compression affect image quality?',
        answer: 'The tool uses intelligent compression that balances file size and quality. You can adjust the quality slider to find the perfect balance for your needs. Higher quality means larger files.',
      },
      {
        question: 'What formats can I compress?',
        answer: 'You can compress PNG, JPG/JPEG, and WebP images. The tool also allows you to change the output format during compression.',
      },
    ],
  },
  {
    category: 'Image Converter',
    questions: [
      {
        question: 'What formats are supported?',
        answer: 'The converter supports PNG, JPG/JPEG, WebP, ICO, and BMP formats. You can convert between any of these formats.',
      },
      {
        question: 'Can I convert multiple images at once?',
        answer: 'Currently, the converter processes one image at a time. We plan to add batch conversion in a future update.',
      },
      {
        question: 'What is WebP and why should I use it?',
        answer: 'WebP is a modern image format developed by Google that provides superior compression. WebP images are typically 25-34% smaller than equivalent JPEG images while maintaining the same quality, making them ideal for web use.',
      },
    ],
  },
  {
    category: 'SVG Editor',
    questions: [
      {
        question: 'What can I edit with the SVG Editor?',
        answer: 'You can edit fill colors, stroke colors, stroke widths, opacity, and transforms of SVG elements. You can select individual elements within your SVG and modify their properties.',
      },
      {
        question: 'Can I add new elements to an SVG?',
        answer: 'The current version focuses on editing existing elements. We plan to add element creation tools in future updates.',
      },
      {
        question: 'Can I undo changes?',
        answer: 'Yes, changes are applied live to the preview. You can reload your original file to reset all changes, or manually revert individual properties.',
      },
    ],
  },
  {
    category: 'Technical & Performance',
    questions: [
      {
        question: 'What browsers are supported?',
        answer: 'ImageLab works on all modern browsers including Chrome, Firefox, Safari, Edge, and Opera. We recommend using the latest version of your preferred browser for the best experience. Internet Explorer is not supported.',
      },
      {
        question: 'Is there a file size limit?',
        answer: 'While there\'s no strict file size limit, extremely large files (over 50MB) may take longer to process. For best performance, we recommend using files under 10MB. If you need to work with very large files, consider splitting them or using a desktop application.',
      },
      {
        question: 'Can I use ImageLab offline?',
        answer: 'The tools require an initial internet connection to load the application, but once loaded, all processing happens locally in your browser. We\'re working on Progressive Web App (PWA) support for true offline functionality.',
      },
      {
        question: 'Why is browser-based processing better than server-based?',
        answer: 'Browser-based processing ensures complete privacy since your files never leave your device. It also means faster processing (no upload/download time), no bandwidth costs, unlimited usage without rate limits, and the ability to work with sensitive files without security concerns.',
      },
      {
        question: 'What technologies power ImageLab?',
        answer: 'ImageLab is built with React 18 and TypeScript for type-safe code, styled with Tailwind CSS for responsive design, and powered by Vite for fast builds. We use HTML5 Canvas API for image processing, FileReader API for file handling, and modern web standards for optimal performance.',
      },
    ],
  },
  {
    category: 'Best Practices & Tips',
    questions: [
      {
        question: 'What image quality should I use for my website?',
        answer: 'For photos, use JPG at 75-85% quality for a good balance of size and quality. For thumbnails, 60-70% is often sufficient. For graphics with text or sharp edges, use PNG or SVG. For modern websites, consider WebP format at 80% quality—it provides JPG-level quality at 25-35% smaller file sizes.',
      },
      {
        question: 'How can I improve my website\'s page speed with image optimization?',
        answer: 'Start by resizing images to their actual display size (don\'t serve 3000px images to display at 300px). Compress all images using our image compressor. Use lazy loading for images below the fold. Serve modern formats like WebP with fallbacks. Implement responsive images with srcset. These steps can reduce image bandwidth by 60-80% and significantly improve Core Web Vitals.',
      },
      {
        question: 'Should I convert all my PNGs to JPG to save space?',
        answer: 'No. PNG should be used for logos, icons, graphics with text, and images requiring transparency. JPG is better for photographs and complex images. Converting a logo to JPG will result in artifacts around text and edges. Use the right format for the right purpose.',
      },
      {
        question: 'How do I choose between inline SVG and SVG as an image?',
        answer: 'Use inline SVG when you need CSS styling, JavaScript manipulation, or best performance for icons. Use SVG as an <img> when you want caching benefits and don\'t need dynamic control. For decorative patterns, use SVG as a CSS background. Inline is best for small, frequently-used icons; external files are better for large illustrations used once.',
      },
      {
        question: 'What\'s the best size for a favicon source image?',
        answer: 'Start with at least 512×512 pixels (1024×1024 is ideal) in PNG or SVG format. This ensures all generated favicon sizes look crisp. Keep the design simple with high contrast—favicons appear as small as 16×16 pixels, so intricate details won\'t be visible at that size.',
      },
    ],
  },
  {
    category: 'Troubleshooting',
    questions: [
      {
        question: 'Why isn\'t my SVG displaying correctly?',
        answer: 'Common issues include: missing viewBox attribute (required for proper scaling), paths using external references that weren\'t included, embedded fonts that aren\'t available in the browser, or invalid SVG syntax. Use our SVG to Code tool to validate and clean your SVG files.',
      },
      {
        question: 'My compressed images look blurry. What should I do?',
        answer: 'You may be compressing too aggressively. Try increasing the quality setting. For JPG, stay above 70% quality. Also ensure you\'ve resized images appropriately first—starting with very large images and compressing aggressively can cause quality issues. For images displayed at 400px, resize to 800px (2x for retina) before compressing.',
      },
      {
        question: 'PNG to SVG conversion isn\'t working well. Why?',
        answer: 'PNG to SVG works best with simple graphics, logos, and icons with clear edges and solid colors. It struggles with photographs, gradients, textures, and blurry images. Ensure your source image has good contrast and clear edges. Consider simplifying the image in a photo editor before conversion for better results.',
      },
      {
        question: 'Why does my generated favicon not update in the browser?',
        answer: 'Browsers heavily cache favicons. Try a hard refresh (Ctrl+Shift+R or Cmd+Shift+R), clear your browser cache, or test in incognito/private mode. Sometimes it takes up to 24 hours for favicon changes to fully propagate. Also verify your HTML link tags are correct and favicon files are uploaded to the right location.',
      },
    ],
  },
];

export const FAQPage = memo(function FAQPage() {
  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold text-white mb-4">Frequently Asked Questions</h1>
      <p className="text-slate-400 mb-8">
        Find answers to common questions about ImageLab and our image tools.
      </p>
      
      <div className="space-y-8">
        {faqs.map((category) => (
          <section key={category.category}>
            <h2 className="text-xl font-semibold text-indigo-400 mb-4 pb-2 border-b border-slate-700">
              {category.category}
            </h2>
            <div className="space-y-3">
              {category.questions.map((faq, index) => (
                <details 
                  key={index} 
                  className="bg-slate-800/50 rounded-lg border border-slate-700 group"
                >
                  <summary className="px-6 py-4 cursor-pointer text-white font-medium list-none flex justify-between items-center hover:bg-slate-800/70 rounded-lg transition-colors">
                    {faq.question}
                    <span className="text-indigo-400 group-open:rotate-180 transition-transform ml-4 flex-shrink-0">
                      ▼
                    </span>
                  </summary>
                  <div className="px-6 pb-4 text-slate-400">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </section>
        ))}
      </div>

      <div className="mt-12 p-6 bg-slate-800/50 rounded-xl border border-slate-700 text-center">
        <h3 className="text-lg font-semibold text-white mb-2">Still have questions?</h3>
        <p className="text-slate-400 mb-4">
          Can't find the answer you're looking for? Reach out to us!
        </p>
        <a 
          href="https://github.com" 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg transition-colors"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
          </svg>
          Ask on GitHub
        </a>
      </div>
    </div>
  );
});
